import type { Skill } from '@atlassian/rovo-platform-types/skills';

import { type ConvoAiSkillNode, parseConvoAiSkillEdges } from '../../utils/parse-skill';

const GRAPHQL_ENDPOINT = '/gateway/api/graphql';

/**
 * Unified GraphQL query — convoai_availableSkills.
 */
const UNIFIED_SKILLS_QUERY = `
  query ConvoAiAvailableSkillsQuery(
    $first: Int
    $cloudId: ID!
  ) {
    convoai_availableSkills(
      first: $first
      cloudId: $cloudId
    )
    @optIn(to: "ConvoAiAvailableSkills")
    {
      edges {
        node {
          __typename
          ... on ConvoAiSkill {
            id
            name
            description
            displayName
            slashCommand
						helpText
            iconUrl
            iconKey
            color
            source
            tags
            category
            integrationKeys
          }
        }
      }
      errors {
        message
      }
    }
  }
`;

interface UnifiedGraphQLResponse {
	data?: {
		convoai_availableSkills?: {
			edges?: ReadonlyArray<{
				node?: ConvoAiSkillNode | null;
			} | null>;
			errors?: Array<{ message: string }> | null;
		} | null;
	};
	errors?: Array<{ message: string }>;
}

/**
 * Session-level cache for fetched skills.
 * Keyed by `${cloudId}` to avoid redundant network requests.
 * Skills are cached for the lifetime of the page (matching Relay store behavior).
 * A page refresh will fetch fresh data.
 */
const skillsCache = new Map<string, Skill[]>();

/**
 * Clears the skills cache. Useful for testing or forcing a refresh.
 */
export function clearSkillsCache(): void {
	skillsCache.clear();
}

/**
 * Fetch skills using the new unified convoai_availableSkills endpoint.
 */
async function fetchSkillsUnified(cloudId: string): Promise<Skill[]> {
	const response = await fetch(GRAPHQL_ENDPOINT, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		credentials: 'include',
		body: JSON.stringify({
			query: UNIFIED_SKILLS_QUERY,
			variables: { cloudId, first: 150 },
		}),
	});

	if (!response.ok) {
		return [];
	}

	const json: UnifiedGraphQLResponse = await response.json();

	if (json.errors?.length || json.data?.convoai_availableSkills?.errors?.length) {
		return [];
	}

	const edges = json.data?.convoai_availableSkills?.edges;
	return parseConvoAiSkillEdges(edges);
}

/**
 * Fetch skills from the GraphQL API.
 *
 * Uses vanilla `fetch` (not Relay) so it can be called outside React
 * in the extension manifest setup.
 *
 * Uses the `convoai_availableSkills` endpoint.
 *
 * Results are cached for the lifetime of the page (session-level cache,
 * matching Relay store behavior). A page refresh will fetch fresh data.
 *
 * @param cloudId - The Atlassian cloud ID for the site
 */
export async function fetchSkills(cloudId: string): Promise<Skill[]> {
	const cacheKey = cloudId;
	const cached = skillsCache.get(cacheKey);
	if (cached) {
		return cached;
	}

	try {
		const skills = await fetchSkillsUnified(cloudId);

		skillsCache.set(cacheKey, skills);
		return skills;
	} catch {
		// Silently fail — the insert menu will just not show skills
		return [];
	}
}
